// multi-line-chart.angular20.ts
import * as d3 from 'd3';
import { ElementRef } from '@angular/core';
import { timeFormatLocale, TimeLocaleDefinition } from 'd3-time-format';

import { UserSettings, I18nService, SettingsUtil } from '@lp-ui-lib';
import { ValueRange, ValueRangeType } from './value-range';
import { Visualization } from './visualization.interface';
import { generateSecureIdentifier } from './visualization-type';

// ---------- Raw input ----------
export interface MultiLineRawPoint {
  timestamp: Date | string;
  value: number;
  measuredValue: string;
  measurementTypeID: number;
}

// ---------- Locales ----------
const LOCALE_EN: TimeLocaleDefinition = {
  dateTime: '%x, %X',
  date: '%-m/%-d/%Y',
  time: '%-I:%M:%S %p',
  periods: ['AM', 'PM'] as [string, string],
  days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  shortDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};
const LOCALE_DE: TimeLocaleDefinition = {
  dateTime: '%A, der %e. %B %Y, %X',
  date: '%d.%m.%Y',
  time: '%H:%M:%S',
  periods: ['AM', 'PM'] as [string, string],
  days: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
  shortDays: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
  months: [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ],
  shortMonths: ['Jan', 'Feb', 'Mrz', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
};

// ---------- Internal types ----------
interface Point {
  date: Date;
  value: number;
  text: string;
}
interface LineSeries {
  id: number;
  data: Point[];
  gradientId: string;
  isDotted: boolean; // IDs 1074, 1025, 1076
  strokeFallback: string; // color when no ranges present
}

export class MultiLineChart implements Visualization<MultiLineRawPoint, UserSettings> {
  // ---------------- State ----------------
  private x_domain: [Date, Date] | null = null; // external override or computed
  private y_domain: [number, number] | null = null;
  private valueRanges: ValueRange[] = [];
  private y_unit = '';
  private booleanDomain = false;
  private customRange = false;

  // all points & split series
  private flatData: Point[] = [];
  private series: LineSeries[] = [];
  private dataByType = new Map<number, Point[]>();

  // UI text from i18n
  public MaxText = 'max';
  public MinText = 'min';

  // override flags
  public warningOverride = false;
  public criticalOverride = false;

  // public hooks
  public updateChart: (() => void) | undefined;
  private resetBrush: (() => void) | undefined;
  private brushCallback: ((x_domain: [Date, Date]) => void) | undefined;
  private brushResetCallback: ((x_domain: [Date, Date]) => void) | undefined;

  constructor(
    private chartContainer: ElementRef<HTMLElement>,
    private id: string,
    private i18n: I18nService
  ) {}

  // ---------------- Visualization API ----------------
  public render(
    data: readonly MultiLineRawPoint[],
    isMobile: boolean,
    settings: UserSettings
  ): void {
    this.ingest(data);
    this.renderGraph(isMobile, settings);
  }

  public SetValueUnit(unit: string): void {
    this.y_unit = unit ?? '';
  }

  public SetXDomain(start: Date, end: Date, custom = false): void {
    // Align right cutoff to next midnight when not custom (same as sample)
    if (!custom && (end.getHours() > 0 || end.getMinutes() > 0)) {
      end = new Date(new Date(end).setDate(end.getDate() + 1));
    }
    if (!custom) start = new Date(start.toDateString());
    if (!custom) end = new Date(end.toDateString());
    this.x_domain = d3.extent([start, end], (d) => d) as [Date, Date];
    this.customRange = custom;
    this.updateChart?.();
  }

  public ResetXDomain(): void {
    this.x_domain = null;
  }

  public SetYDomain(domain: [number, number], booleanDomain = false): void {
    if (booleanDomain) {
      this.y_domain = [-0.4, 1.4];
      this.booleanDomain = true;
    } else {
      if (domain[1] > domain[0]) this.y_domain = domain;
      this.booleanDomain = false;
    }
    this.updateChart?.();
  }

  public ResetYDomain(): void {
    this.y_domain = null;
  }

  public SetValueRanges(ranges: readonly ValueRange[]): void {
    this.valueRanges = (ranges ?? []) as ValueRange[];
  }

  public OnBrush(callback: (x_domain: [Date, Date]) => void): void {
    this.brushCallback = callback;
  }

  public OnBrushReset(callback: (x_domain: [Date, Date]) => void): void {
    this.brushResetCallback = callback;
  }

  public ResetBrush(): void {
    this.resetBrush?.();
  }

  // ---------------- Data ingest & prep ----------------
  private ingest(data: readonly MultiLineRawPoint[]): void {
    this.flatData = [];
    this.dataByType.clear();

    const incoming = data ?? [];
    for (const t of incoming) {
      const val = Number(t.value.toFixed(2));
      const p: Point = {
        date: new Date(t.timestamp),
        value: val,
        text: t.measuredValue,
      };
      this.flatData.push(p);
      if (!this.dataByType.has(t.measurementTypeID)) {
        this.dataByType.set(t.measurementTypeID, []);
      }
      this.dataByType.get(t.measurementTypeID)!.push(p);
    }

    // sort series
    for (const arr of this.dataByType.values()) {
      arr.sort((a, b) => +a.date - +b.date);
    }

    // Build series metadata
    this.series = Array.from(this.dataByType.entries()).map(([id, arr]) => ({
      id,
      data: arr,
      gradientId: `ml-grad-${generateSecureIdentifier()}-${id}`,
      isDotted: id === 1074 || id === 1025 || id === 1076,
      strokeFallback: id === 1074 || id === 1025 || id === 1076 ? '#5F6973' : 'black',
    }));

    // Legend i18n mapping
    const ids = new Set(this.series.map((s) => s.id.toString()));
    if (ids.has('1073')) {
      this.MaxText = this.i18n.langString('inlet');
      this.MinText = this.i18n.langString('outlet');
    } else {
      this.MaxText = this.i18n.langString('max');
      this.MinText = this.i18n.langString('min');
    }
  }

  // ---------------- Rendering ----------------
  private renderGraph(isMobile: boolean, settings: UserSettings): void {
    if (!this.chartContainer) return;

    d3.select(`svg#${this.id}`).remove();

    const element = this.chartContainer.nativeElement;
    const max_width = element.clientWidth;
    const max_height = element.clientHeight;
    const isSafari = navigator.userAgent.indexOf('Safari') !== -1;

    const margin = { top: 55, right: 85, bottom: 71, left: 0 };
    const width = max_width - margin.left - margin.right;
    const height = max_height - margin.top - margin.bottom;

    if (height < 0) return;

    // Selections
    let svg!: d3.Selection<SVGGElement, unknown, null, undefined>;
    let xAxis!: d3.Selection<SVGGElement, unknown, null, undefined>;
    let yAxis!: d3.Selection<SVGGElement, unknown, null, undefined>;
    let yearAxis!: d3.Selection<SVGGElement, unknown, null, undefined>;
    let clipMask!: d3.Selection<SVGGElement, unknown, null, undefined>;

    // Focus & value UI
    let focusLine!: d3.Selection<SVGLineElement, unknown, null, undefined>;
    let focusDate!: d3.Selection<SVGTextElement, unknown, null, undefined>;
    let focusDateBG!: d3.Selection<SVGRectElement, unknown, null, undefined>;
    let focusValues: d3.Selection<SVGTextElement, unknown, null, undefined>[] = [];
    let focusValueLines: d3.Selection<SVGLineElement, unknown, null, undefined>[] = [];
    let focusValuesBG: d3.Selection<SVGRectElement, unknown, null, undefined>[] = [];
    let circlePointsInner: d3.Selection<SVGCircleElement, unknown, null, undefined>[] = [];
    let circlePointsOuter: d3.Selection<SVGCircleElement, unknown, null, undefined>[] = [];

    // Scales, extents, helpers
    let x!: d3.ScaleTime<number, number>;
    let y!: d3.ScaleLinear<number, number>;

    let x_extent!: [Date, Date];
    let y_extent!: [number, number];

    let bisect!: (data: Point[], xMs: number) => number;

    let sx!: number; // focus x
    let sy: number[] = []; // per-line focus y
    let noData!: boolean;

    const drawChart = () => {
      init();
      drawAxes();
      drawClipmask();
      drawHorizontalLines();
      if (!noData) drawRanges();
      if (!noData) drawLegend();
      if (!noData) drawAreaBetweenCurves();
      if (!noData) drawCurves();
      if (!noData) drawFocus();
      initBrushing();
      if (noData) drawNoDataIndicator();
    };

    const init = () => {
      noData = (this.flatData?.length ?? 0) < 1;

      // fallback data if empty to avoid errors (same spirit as legacy)
      if (noData) {
        this.flatData = [
          { date: new Date(new Date().setDate(new Date().getDate() - 7)), value: 0, text: '0' },
          { date: new Date(), value: 100, text: '100' },
        ];
      }

      x_extent = d3.extent(this.flatData, (d) => d.date) as [Date, Date];
      if (!this.x_domain) this.x_domain = [x_extent[0], x_extent[1]];

      const partial = this.flatData.filter(
        (d) => d.date >= this.x_domain![0] && d.date <= this.x_domain![1]
      );

      const temp: [number, number] = [
        d3.min(partial, (d) => +d.value) ?? 0,
        d3.max(partial, (d) => +d.value) ?? 1,
      ];

      y_extent = this.y_domain
        ? [Math.min(temp[0], this.y_domain[0]), Math.max(temp[1], this.y_domain[1])]
        : temp;

      // avoid identical min/max
      if (y_extent[0] === y_extent[1]) {
        const e = 0.1 * (y_extent[0] || 1);
        y_extent = [y_extent[0] - e, y_extent[0] + e];
      }

      const buffer = this.booleanDomain ? 0 : (y_extent[1] - y_extent[0]) * 0.1;

      x = d3
        .scaleTime()
        .domain(this.x_domain!)
        .range([20, width - 20]);
      y = d3
        .scaleLinear()
        .domain([y_extent[0] - buffer, y_extent[1] + buffer])
        .range([height, 0]);

      bisect = (data: Point[], xMs: number) => {
        const right = d3.bisector<Point, number>((d) => +d.date).right(data, xMs);
        if (right === 0) return right;
        const left = right - 1;
        if (!data[right]) return left;
        const diff1 = xMs - +data[left].date;
        const diff2 = +data[right].date - xMs;
        return diff1 < diff2 ? left : right;
      };

      const last = this.flatData[this.flatData.length - 1];
      sx = x(last.date);

      initSVG();
    };

    const initSVG = () => {
      const containerSvg = d3
        .select(element)
        .append('svg')
        .attr('id', this.id)
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom);

      svg = containerSvg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

      const defs = svg.append('defs');
      const filter = defs
        .append('filter')
        .attr('id', 'pill-gradient')
        .attr('height', '1000%')
        .attr('width', '1000%')
        .attr('x', '-500%')
        .attr('y', '-500%');

      filter
        .append('feColorMatrix')
        .attr('type', 'matrix')
        .attr('values', '0 0 0 1 0  0 0 0 1 0  0 0 0 1 0  0 0 0 1 0');

      filter.append('feGaussianBlur').attr('stdDeviation', 2).attr('result', 'coloredBlur');

      const feMerge = filter.append('feMerge');
      feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    };

    const drawClipmask = () => {
      svg
        .append('defs')
        .append('clipPath')
        .attr('id', `multiline-clip-${this.id}`)
        .append('rect')
        .attr('width', width)
        .attr('height', height + 10)
        .attr('x', 0)
        .attr('y', -5);

      clipMask = svg.append('g').attr('clip-path', `url(#multiline-clip-${this.id})`);
    };

    const drawAxes = () => {
      // background
      svg
        .append('rect')
        .style('fill', '#ffffff')
        .style('cursor', 'pointer')
        .attr('x', -1)
        .attr('y', 0)
        .attr('width', width + 1)
        .attr('height', height);

      if (!noData) {
        xAxis = svg
          .append('g')
          .attr('id', 'x-axis')
          .call(
            d3
              .axisBottom(x)
              .tickSize(height)
              .tickPadding(10)
              .tickFormat(() => '')
          )
          .call((g) =>
            g.selectAll('.tick line').attr('stroke', '#e6e7e7').style('cursor', 'pointer')
          )
          .call((g) => g.select('.domain').remove())
          .call((g) =>
            g
              .selectAll('.tick text')
              .attr('fill', '#afb4b9')
              .attr('font-size', 12)
              .attr('font-family', 'Roboto')
              .attr('font-weight', '700')
              .attr('line-height', 18 as any)
              .attr('letter-spacing', 0 as any)
              .attr('transform', 'translate(0,-3)')
          );

        yearAxis = svg
          .append('g')
          .attr('id', 'year-axis')
          .call(
            d3
              .axisBottom(x)
              .tickSize(height + 28)
              .ticks(d3.timeYear)
              .tickPadding(15)
              .tickFormat((d: any) => d3.timeFormat('%Y')(d))
          )
          .call((g) => g.selectAll('.tick line').attr('stroke', '#e6e7e7'))
          .call((g) => g.select('.domain').remove())
          .call((g) => g.selectAll('.tick text').attr('fill', '#afb4b9'));
      }

      yAxis = svg
        .append('g')
        .attr('id', 'y-axis')
        .attr('transform', `translate(${width}, 0)`)
        .call(
          this.booleanDomain
            ? d3
                .axisRight(y)
                .tickValues([0, 1])
                .tickFormat((d: any) => d)
            : d3
                .axisRight(y)
                .tickValues([y_extent[0], y_extent[1]])
                .tickFormat((d: any) => (noData || d === y_extent[0] ? '' : `${d} ${this.y_unit}`))
        )
        .call((g) => g.select('.domain').remove())
        .call((g) => g.selectAll('.tick line').remove())
        .call((g) =>
          g
            .selectAll('.tick text')
            .attr('x', 4)
            .attr('fill', '#afb4b9')
            .attr('font-size', 12)
            .attr('font-family', 'Roboto')
            .attr('font-weight', '700')
            .attr('line-height', 18 as any)
            .attr('letter-spacing', 0 as any)
            .attr('text-anchor', 'end')
            .attr('dx', 35)
        );

      // boolean icons
      if (this.booleanDomain) {
        yAxis.selectAll('.tick text').remove();

        yAxis
          .selectAll('.tick:first-child')
          .append('svg:image')
          .attr('xlink:href', 'assets/icons/tilt_untilted.svg')
          .attr('width', 48)
          .attr('height', 48)
          .attr('x', 4)
          .attr('y', -36);

        yAxis
          .selectAll('.tick:last-child')
          .append('svg:image')
          .attr('xlink:href', 'assets/icons/tilt_tilted_red.svg')
          .attr('width', 48)
          .attr('height', 48)
          .attr('x', 4)
          .attr('y', -32);
      }
    };

    const drawHorizontalLines = () => {
      clipMask
        .append('line')
        .attr('x1', 0)
        .attr('x2', width)
        .attr('y1', y(0))
        .attr('y2', y(0))
        .attr('stroke', this.booleanDomain ? '#000000' : '#979b9b');

      clipMask
        .append('line')
        .attr('x1', 0)
        .attr('x2', width)
        .attr('y1', height)
        .attr('y2', height)
        .attr('stroke', '#e6e7e7');

      svg
        .append('line')
        .attr('x1', 0)
        .attr('x2', max_width)
        .attr('y1', height + 31)
        .attr('y2', height + 31)
        .attr('stroke', '#e6e7e7');
    };

    const drawNoDataIndicator = () => {
      svg.append('text').text('No data').attr('y', 16).attr('x', 36).style('opacity', 0.6);
    };

    const drawRanges = () => {
      // Draw per-line gradient using ranges filtered for that measurementTypeID

      this.series.forEach((s) => {
        const rangesForThis = this.valueRanges.filter((r) => r.measurementTypeID === s.id);
        const lastVal = s.data.length ? s.data[s.data.length - 1].value : 0;

        const warn =
          this.warningOverride ||
          !!rangesForThis.find(
            (r) => r.type === ValueRangeType.yellow && r.min <= lastVal && r.max >= lastVal
          );
        const crit =
          this.criticalOverride ||
          !!rangesForThis.find(
            (r) => r.type === ValueRangeType.red && r.min <= lastVal && r.max >= lastVal
          );

        svg
          .select('defs')
          .append('linearGradient')
          .attr('id', s.gradientId)
          .attr('gradientUnits', 'userSpaceOnUse')
          .attr('x1', 0)
          .attr('y1', y(y_extent[0]))
          .attr('x2', 0)
          .attr('y2', y(y_extent[1]))
          .selectAll('stop')
          .data(generateCriticalGradientForLine(rangesForThis, lastVal, y_extent, crit, warn))
          .enter()
          .append('stop')
          .attr('offset', (d: any) => d.offset)
          .attr('stop-color', (d: any) => d.color);
      });
    };

    const drawLegend = () => {
      // left solid line + MaxText
      svg
        .append('line')
        .attr('x1', 9)
        .attr('x2', 31)
        .attr('y1', -31)
        .attr('y2', -31)
        .attr('stroke', 'black')
        .attr('stroke-width', 3);

      svg
        .append('text')
        .text(this.MaxText)
        .attr('font-size', 12)
        .attr('font-family', 'Roboto')
        .attr('font-weight', '700')
        .attr('line-height', 18 as any)
        .attr('letter-spacing', 0 as any)
        .attr('dy', -4)
        .attr('class', 'brushText2Time')
        .attr('text-anchor', 'middle')
        .attr('alignment-baseline', 'middle')
        .attr('y', -23)
        .attr('x', 51);

      // dashed line + MinText
      svg
        .append('line')
        .attr('x1', 91)
        .attr('x2', 118)
        .attr('y1', -31)
        .attr('y2', -31)
        .attr('stroke', 'black')
        .attr('stroke-width', 3)
        .style('stroke-dasharray', '3, 5');

      svg
        .append('text')
        .text(this.MinText)
        .attr('font-size', 12)
        .attr('font-family', 'Roboto')
        .attr('font-weight', '700')
        .attr('line-height', 18 as any)
        .attr('letter-spacing', 0 as any)
        .attr('dy', -4)
        .attr('class', 'brushText2Time')
        .attr('text-anchor', 'middle')
        .attr('alignment-baseline', 'middle')
        .attr('y', -23)
        .attr('x', 142);
    };

    let areaData: { date: Date; minVal: number; maxVal: number }[] | null = null;
    const drawAreaBetweenCurves = () => {
      // only when exactly 2 lines and NOT including 1073/1074 (keep your legacy rule)
      const ids = this.series.map((s) => s.id.toString());
      if (ids.includes('1073') || ids.includes('1074')) return;
      if (this.series.length !== 2) return;

      const s0 = this.series[0].data;
      const s1 = this.series[1].data;

      const unionX = Array.from(new Set([...s0.map((p) => +p.date), ...s1.map((p) => +p.date)]))
        .sort((a, b) => a - b)
        .map((ms) => new Date(ms));

      areaData = unionX.map((date) => {
        const i0 = bisect(s0, +date);
        const i1 = bisect(s1, +date);
        const v0 = s0[i0]?.value ?? s0[Math.max(0, s0.length - 1)]?.value ?? 0;
        const v1 = s1[i1]?.value ?? s1[Math.max(0, s1.length - 1)]?.value ?? 0;
        const minVal = Math.min(v0, v1);
        const maxVal = Math.max(v0, v1);
        return { date, minVal, maxVal };
      });

      clipMask
        .append('path')
        .datum(areaData)
        .attr('class', 'area')
        .attr('fill', '#cce5df')
        .attr('stroke', '#69b3a2')
        .attr('stroke-width', 1.5)
        .attr(
          'd',
          (isSafari
            ? d3
                .area<{ date: Date; minVal: number; maxVal: number }>()
                .x((d) => x(d.date))
                .y0((d) => y(d.minVal))
                .y1((d) => y(d.maxVal))
            : d3
                .area<{ date: Date; minVal: number; maxVal: number }>()
                .x((d) => x(d.date))
                .y0((d) => y(d.minVal))
                .y1((d) => y(d.maxVal))
                .curve(d3.curveMonotoneX)) as any
        );
    };

    const drawCurves = () => {
      this.series.forEach((s) => {
        clipMask
          .append('path')
          .datum(s.data)
          .attr('class', s.isDotted ? 'curve dotted' : 'curve')
          .attr('fill', 'none')
          .attr('stroke', this.valueRanges?.length ? `url(#${s.gradientId})` : s.strokeFallback)
          .attr('stroke-width', 2)
          .style('stroke-dasharray', s.isDotted ? '2,2' : 'initial')
          .style('stroke-dashoffset', s.isDotted ? '2' : 'initial')
          .style('cursor', 'pointer')
          .attr(
            'd',
            (isSafari
              ? d3
                  .line<Point>()
                  .x((d) => x(d.date))
                  .y((d) => y(d.value))
              : d3
                  .line<Point>()
                  .x((d) => x(d.date))
                  .y((d) => y(d.value))
                  .curve(d3.curveMonotoneX)) as any
          );
      });
    };

    const drawFocus = () => {
      if (!this.flatData?.length) return;

      const yTop = y(
        this.y_domain ? this.y_domain[1] : (d3.max(this.flatData, (d) => +d.value) ?? 0)
      );
      const yBot = y(
        this.y_domain ? this.y_domain[0] : (d3.min(this.flatData, (d) => +d.value) ?? 0)
      );

      focusLine = svg
        .append('line')
        .attr('x1', sx)
        .attr('y1', yTop)
        .attr('x2', sx)
        .attr('y2', yBot)
        .attr('stroke', 'black')
        .style('cursor', 'pointer');

      // date pill below
      focusDateBG = svg
        .append('rect')
        .style('fill', 'white')
        .attr('x', sx)
        .attr('y', height + 7)
        .attr('width', 35)
        .attr('height', 12)
        .style('filter', 'url(#pill-gradient)')
        .attr('rx', '3')
        .attr('ry', '3')
        .style('transform', 'translate(-18px, 0px)');

      const dateFmt =
        !!SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID) &&
        SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID)?.formatString ===
          'MM/dd/yyyy'
          ? '%m/%d'
          : '%e-%m';

      focusDate = svg
        .append('text')
        .text(d3.timeFormat(dateFmt)(this.flatData[this.flatData.length - 1].date))
        .attr('font-size', 12)
        .attr('font-family', 'Roboto')
        .attr('font-weight', '700')
        .attr('line-height', 18 as any)
        .attr('letter-spacing', 0 as any)
        .attr('dy', -4)
        .attr('class', 'brushText2Time')
        .attr('text-anchor', 'middle')
        .attr('alignment-baseline', 'middle')
        .attr('y', height + (isSafari ? 16 : 21))
        .attr('x', sx);

      // per-line values + circles (F2 smart layout)
      focusValues = [];
      focusValuesBG = [];
      focusValueLines = [];
      circlePointsInner = [];
      circlePointsOuter = [];

      // Place initial markers at right edge (latest date)
      const x0 = this.flatData[this.flatData.length - 1].date;
      const selected = this.series.map((s) => {
        const i = bisect(s.data, +x0);
        return s.data[i];
      });

      // compute warning/critical per line at x0
      const warn = this.series.map((s, i) => {
        const ranges = this.valueRanges.filter((r) => r.measurementTypeID === s.id);
        return (
          this.warningOverride ||
          !!ranges.find(
            (r) =>
              r.type === ValueRangeType.yellow &&
              r.min <= selected[i].value &&
              r.max >= selected[i].value
          )
        );
      });
      const crit = this.series.map((s, i) => {
        const ranges = this.valueRanges.filter((r) => r.measurementTypeID === s.id);
        return (
          this.criticalOverride ||
          !!ranges.find(
            (r) =>
              r.type === ValueRangeType.red &&
              r.min <= selected[i].value &&
              r.max >= selected[i].value
          )
        );
      });

      sy = selected.map((p) => y(p.value));

      // smart spacing to avoid overlap (F2)
      const sortedIdx = d3.range(this.series.length).sort((a, b) => sy[a] - sy[b]);
      const minGap = 18;
      for (let k = 1; k < sortedIdx.length; k++) {
        const prev = sortedIdx[k - 1];
        const curr = sortedIdx[k];
        if (sy[curr] - sy[prev] < minGap) {
          sy[curr] = sy[prev] + minGap;
        }
      }

      this.series.forEach((s, i) => {
        const col = crit[i] ? '#E1000F' : warn[i] ? '#FFCC33' : '#00AA75';

        focusValuesBG.push(
          svg
            .append('rect')
            .style('fill', 'white')
            .attr('x', width)
            .attr('y', sy[i] - 6)
            .attr('width', 46)
            .attr('height', 12)
            .attr('opacity', 0)
            .style('filter', 'url(#pill-gradient)')
            .attr('rx', '3')
            .attr('ry', '3')
        );

        focusValues.push(
          svg
            .append('text')
            .text(`${selected[i].value}${this.y_unit}`)
            .attr('x', width)
            .attr('y', sy[i])
            .attr('fill', col)
            .attr('font-size', 12)
            .attr('font-family', 'Roboto')
            .attr('font-weight', '700')
            .attr('line-height', 18 as any)
            .attr('letter-spacing', 0 as any)
            .attr('text-anchor', 'end')
            .attr('dx', 40)
            .attr('dy', 4)
        );

        focusValueLines.push(
          svg
            .append('line')
            .attr('x1', sx)
            .attr('x2', width - 5)
            .attr('y1', sy[i])
            .attr('y2', sy[i])
            .attr('stroke', col)
            .attr('opacity', 0) // hidden by default (legacy had 0)
        );

        circlePointsInner.push(
          svg
            .append('circle')
            .attr('class', 'inner')
            .attr('fill', col)
            .attr('cx', sx)
            .attr('cy', sy[i])
            .attr('r', 8)
        );

        circlePointsOuter.push(
          svg
            .append('circle')
            .attr('class', 'outer')
            .attr('cx', sx)
            .attr('cy', sy[i])
            .attr('fill', 'transparent')
            .attr('stroke', col)
            .attr('r', 12)
        );
      });
    };

    // -------- Brushing --------
    const initBrushing = () => {
      let unbrushed: [Date, Date] | null = null;

      const brush = d3
        .brushX()
        .extent([
          [0, -6],
          [sx, height + 12],
        ])
        .on('end', (event: d3.D3BrushEvent<any>) => {
          if (!event.sourceEvent) return;
          const extent = event.selection as [number, number] | null;
          const epsilon = 0.008 * Math.abs(+this.x_domain![1] - +this.x_domain![0]);

          if (
            !extent ||
            !extent[0] ||
            Math.abs(+x.invert(extent[1]) - +x.invert(extent[0])) < epsilon
          ) {
            const px = extent ? extent[0] : (event.sourceEvent as MouseEvent).offsetX;
            // move focus (update labels + line) at px
            updateFocusline(x.invert(px), false);
            updateYAxisValuesAt(x.invert(px));
          } else {
            if (minutesBetween(x.invert(extent[0]), x.invert(extent[1])) > 60) {
              if (!unbrushed) unbrushed = this.x_domain!;
              this.SetXDomain(x.invert(extent[0]), x.invert(extent[1]), true);
              this.brushCallback?.(this.x_domain!);
            }
          }
          // clear grey selection
          setTimeout(() => svg.select('.brush').call(brush.move as any, null));
        });

      this.resetBrush = () => {
        if (unbrushed) this.SetXDomain(unbrushed[0], unbrushed[1]);
        unbrushed = null;
        this.brushResetCallback?.(this.x_domain!);

        const last = this.flatData[this.flatData.length - 1];
        updateFocusline(last.date, true);
        updateYAxisValuesAt(last.date);
      };

      svg.on('dblclick', () => this.resetBrush?.());
      clipMask
        .append('g')
        .attr('class', 'brush')
        .call(brush as any);
      clipMask.selectAll('.selection').attr('fill-opacity', 1);
    };

    // -------- Updates --------
    this.updateChart = () => {
      if (!this.flatData?.length) return;
      updateXAxis();
      updateYAxis();
      updateAreaBetweenCurves();
      updateCurves();
      updateFocusPoint();
      mouseout();
    };

    const updateAreaBetweenCurves = () => {
      if (!areaData) return;
      clipMask
        .selectAll('.area')
        .transition()
        .duration(1000)
        .attr(
          'd',
          (isSafari
            ? d3
                .area<{ date: Date; minVal: number; maxVal: number }>()
                .x((d) => x(d.date))
                .y0((d) => y(d.minVal))
                .y1((d) => y(d.maxVal))
            : d3
                .area<{ date: Date; minVal: number; maxVal: number }>()
                .x((d) => x(d.date))
                .y0((d) => y(d.minVal))
                .y1((d) => y(d.maxVal))
                .curve(d3.curveMonotoneX)) as any
        );
    };

    const updateCurves = () => {
      clipMask
        .selectAll<SVGPathElement, Point[]>('.curve')
        .transition()
        .duration(1000)
        .attr(
          'd',
          (isSafari
            ? d3
                .line<Point>()
                .x((d) => x(d.date))
                .y((d) => y(d.value))
            : d3
                .line<Point>()
                .x((d) => x(d.date))
                .y((d) => y(d.value))
                .curve(d3.curveMonotoneX)) as any
        );
    };

    const mouseout = () => {
      if (!this.flatData?.length) return;

      const last = this.flatData[this.flatData.length - 1].date;
      updateFocusline(last, true);
      updateYAxisValuesAt(last);

      focusValueLines.forEach((l) => l.attr('opacity', 0));
    };

    const updateFocusline = (x0: Date, reset = false) => {
      if (!this.flatData?.length) return;

      const selected = this.series.map((s) => {
        const i = bisect(s.data, +x0);
        return s.data[i];
      });

      let _sx = x(selected[0].date);
      _sx = Math.max(0, Math.min(width, _sx));

      focusLine
        .transition()
        .duration(reset ? 1000 : 500)
        .attr('x1', _sx)
        .attr('x2', _sx);

      const warn = this.series.map((s, i) => {
        const ranges = this.valueRanges.filter((r) => r.measurementTypeID === s.id);
        return (
          this.warningOverride ||
          !!ranges.find(
            (r) =>
              r.type === ValueRangeType.yellow &&
              r.min <= selected[i].value &&
              r.max >= selected[i].value
          )
        );
      });

      const crit = this.series.map((s, i) => {
        const ranges = this.valueRanges.filter((r) => r.measurementTypeID === s.id);
        return (
          this.criticalOverride ||
          !!ranges.find(
            (r) =>
              r.type === ValueRangeType.red &&
              r.min <= selected[i].value &&
              r.max >= selected[i].value
          )
        );
      });

      const dateFmt =
        SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID)?.formatString ===
        'MM/dd/yyyy'
          ? '%m/%d'
          : '%e-%m';

      focusDate
        .text(d3.timeFormat(dateFmt)(selected[0].date))
        .transition()
        .duration(reset ? 1000 : 500)
        .attr('x', _sx);

      focusDateBG
        .transition()
        .duration(reset ? 1000 : 500)
        .attr('x', _sx);

      const targetY = selected.map((p) => y(p.value));
      const idxs = d3.range(targetY.length).sort((a, b) => targetY[a] - targetY[b]);
      const minGap = 18;

      for (let k = 1; k < idxs.length; k++) {
        const prev = idxs[k - 1];
        const curr = idxs[k];
        if (targetY[curr] - targetY[prev] < minGap) {
          targetY[curr] = targetY[prev] + minGap;
        }
      }

      selected.forEach((p, i) => {
        const col = crit[i] ? '#E1000F' : warn[i] ? '#FFCC33' : '#00AA75';

        circlePointsInner[i]
          .transition()
          .duration(reset ? 1000 : 500)
          .attr('cx', _sx)
          .attr('cy', targetY[i])
          .attr('fill', col);

        circlePointsOuter[i]
          .transition()
          .duration(reset ? 1000 : 500)
          .attr('cx', _sx)
          .attr('cy', targetY[i])
          .attr('stroke', col);

        focusValues[i]
          .text(`${p.value}${this.y_unit}`)
          .transition()
          .duration(reset ? 1000 : 500)
          .attr('y', targetY[i])
          .attr('fill', col)
          .attr('opacity', 1);

        focusValuesBG[i]
          .transition()
          .duration(reset ? 1000 : 500)
          .attr('y', targetY[i] - 6)
          .attr('opacity', 1);

        focusValueLines[i]
          .transition()
          .duration(reset ? 1000 : 500)
          .attr('x1', _sx)
          .attr('y1', targetY[i])
          .attr('y2', targetY[i])
          .attr('stroke', col)
          .attr('opacity', 0);
      });
    };

    const updateYAxisValuesAt = (_x0: Date) => {
      if (!this.flatData?.length) return;
      updateYAxis();
    };

    const updateXAxis = () => {
      if (this.x_domain) x.domain(this.x_domain);

      const tickCall = generateTicks(
        this.x_domain![0],
        this.x_domain![1],
        width,
        height,
        isMobile,
        this.flatData,
        settings
      );

      xAxis
        .transition()
        .duration(1000)
        .call(tickCall)
        .call((g) => g.selectAll('.tick line').attr('stroke', '#e6e7e7'))
        .call((g) => g.select('.domain').remove())
        .call((g) =>
          g
            .selectAll('.tick text')
            .attr('fill', '#afb4b9')
            .attr('font-size', isMobile ? '12px' : '14px')
            .attr('font-family', 'Roboto')
            .attr('font-weight', '700')
            .attr('line-height', 18 as any)
            .attr('letter-spacing', 0 as any)
            .attr('transform', 'translate(0,-3)')
        );

      const yearTransitionInRange =
        d3.timeYear.range(this.x_domain![0], this.x_domain![1]).length > 0;

      yearAxis
        .transition()
        .duration(1000)
        .call(
          d3
            .axisBottom(x)
            .tickSize(height + (isSafari ? 30 : 28))
            .tickValues(
              yearTransitionInRange
                ? d3.timeYear.range(this.x_domain![0], this.x_domain![1])
                : [new Date((+this.x_domain![0] + +this.x_domain![1]) / 2)]
            )
            .tickPadding(15)
            .tickFormat((d: any) => (noData ? '' : d3.timeFormat('%Y')(d)))
        )
        .call((g) => g.selectAll('.tick line').attr('stroke', 'transparent'))
        .call((g) => g.select('.domain').remove())
        .call((g) =>
          g
            .selectAll('.tick text')
            .attr('fill', '#afb4b9')
            .attr('font-size', 12)
            .attr('font-family', 'Roboto')
            .attr('font-weight', '700')
            .attr('line-height', 18 as any)
            .attr('letter-spacing', 0 as any)
            .attr('dx', yearTransitionInRange ? 0 : (margin.right - margin.left) / 2)
        )
        .call((g) => g.selectAll('.tick:last-child text').attr('fill', 'black'));
    };

    const updateYAxis = () => {
      if (this.booleanDomain) return;

      const partial = this.flatData.filter(
        (d) => d.date > this.x_domain![0] && d.date < this.x_domain![1]
      );
      if (partial.length < 2) return;

      const temp: [number, number] = [
        d3.min(partial, (d) => +d.value) ?? 0,
        d3.max(partial, (d) => +d.value) ?? 1,
      ];

      y_extent = this.y_domain
        ? [Math.min(temp[0], this.y_domain[0]), Math.max(temp[1], this.y_domain[1])]
        : temp;

      const buffer = (y_extent[1] - y_extent[0]) * 0.1;
      if (y_extent[0] < y_extent[1]) {
        y.domain([y_extent[0] - buffer, y_extent[1] + buffer]);
      }

      // Redraw ticks (hide nothing by default)
      yAxis
        .transition()
        .duration(1000)
        .call(
          d3
            .axisRight(y)
            .tickValues([y_extent[0], y_extent[1]])
            .tickFormat((d: any) => (d === y_extent[0] ? '' : `${d} ${this.y_unit}`))
        )
        .call((g) => g.select('.domain').remove())
        .call((g) =>
          g
            .selectAll('.tick text')
            .attr('x', 4)
            .attr('fill', '#afb4b9')
            .attr('font-size', 12)
            .attr('font-family', 'Roboto')
            .attr('font-weight', '700')
            .attr('line-height', 18 as any)
            .attr('letter-spacing', 0 as any)
            .attr('text-anchor', 'end')
            .attr('dx', 35)
        );
      yAxis.call((g) => g.selectAll('.tick line').remove());
    };

    const updateFocusPoint = () => {
      if (!this.flatData?.length) return;
      const rightDate = this.x_domain![1];
      updateFocusline(rightDate, true);
    };

    // draw!
    drawChart();
    setTimeout(() => this.updateChart?.(), 0);
  }
}

// ------------------- Utilities -------------------

function generateCriticalGradientForLine(
  ranges: ValueRange[],
  value: number,
  y_extent: [number, number],
  critical: boolean,
  warning: boolean
) {
  // Keep legacy behavior: green + local crit/warn banding
  ranges = ranges.filter(
    (r) => r.type === ValueRangeType.green || (r.min < value && r.max > value)
  );

  if (!critical && !warning) {
    return [
      { offset: '-100%', color: 'black' },
      { offset: '100%', color: 'black' },
    ];
  }

  const fullRange = y_extent[1] - y_extent[0];
  const offset = fullRange * 0.05;

  const aboveCrit = ranges.find(
    (rr) =>
      rr.type === (critical ? ValueRangeType.red : ValueRangeType.yellow) &&
      !!ranges.find((gg) => gg.type === ValueRangeType.green && gg.max <= rr.min)
  );
  const belowCrit = ranges.find(
    (rr) =>
      rr.type === (critical ? ValueRangeType.red : ValueRangeType.yellow) &&
      !!ranges.find((gg) => gg.type === ValueRangeType.green && gg.min >= rr.max)
  );

  const stops: { offset: string; color: string }[] = [];
  if (belowCrit) {
    const start = belowCrit.max - offset - y_extent[0];
    const end = belowCrit.max + offset - y_extent[0];
    stops.push({ offset: ((start / fullRange) * 100).toFixed(0) + '%', color: '#E1000F' });
    stops.push({ offset: ((end / fullRange) * 100).toFixed(0) + '%', color: 'black' });
  }
  if (aboveCrit) {
    const start = aboveCrit.min - offset - y_extent[0];
    const end = aboveCrit.min + offset - y_extent[0];
    stops.push({ offset: ((start / fullRange) * 100).toFixed(0) + '%', color: 'black' });
    stops.push({ offset: ((end / fullRange) * 100).toFixed(0) + '%', color: '#E1000F' });
  }

  return [
    { offset: '-100%', color: belowCrit ? '#E1000F' : 'black' },
    ...stops,
    { offset: '100%', color: aboveCrit ? '#E1000F' : 'black' },
  ];
}

function treatAsUTC(date: Date | string): number {
  const result = new Date(date);
  result.setMinutes(result.getMinutes() - result.getTimezoneOffset());
  return +result;
}
function daysBetween(startDate: Date, endDate: Date): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  return (treatAsUTC(endDate) - treatAsUTC(startDate)) / msPerDay;
}
function hoursBetween(startDate: Date, endDate: Date): number {
  const msPerHour = 60 * 60 * 1000;
  return (treatAsUTC(endDate) - treatAsUTC(startDate)) / msPerHour;
}
function minutesBetween(startDate: Date, endDate: Date): number {
  const msPerMin = 60 * 1000;
  return (treatAsUTC(endDate) - treatAsUTC(startDate)) / msPerMin;
}
function minDate(a: Date, b: Date) {
  return a < b ? a : b;
}

/** Countable every() safe fallback (kept for parity) */
function everyOr(interval: d3.CountableTimeInterval, step: number): d3.TimeInterval {
  return interval.every(step) ?? interval;
}

/** Tick generator aligned with your Angular 20 LineChart style */
function generateTicks(
  start: Date,
  end: Date,
  width: number,
  height: number,
  isMobile: boolean,
  data: Point[],
  settings: UserSettings
) {
  const small_screen = width < 450;
  const timespan_days = daysBetween(start, end);
  const timespan_hours = hoursBetween(start, end);

  const abbr =
    SettingsUtil.getLanguage(settings.languages, settings.languageID)?.abbreviation ?? 'en';
  const locale = timeFormatLocale(abbr === 'de' ? LOCALE_DE : LOCALE_EN);

  let ticks: d3.TimeInterval = everyOr(
    d3.timeDay as d3.CountableTimeInterval,
    small_screen ? 2 : 1
  );

  let tickFormat: (d: Date) => string = (d) =>
    d3.timeFormat(
      !!SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID) &&
        SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID)?.formatString ===
          'MM/dd/yyyy'
        ? '%m/%d'
        : '%e-%m'
    )(d);

  const tickValuesRangeEnd = minDate(new Date(data[data.length - 1].date), end);
  const tickValuesRangeStart = start;

  if (timespan_days > 15) {
    ticks = everyOr(d3.timeWeek as d3.CountableTimeInterval, 1);
    tickFormat = (d) => (abbr === 'de' ? 'KW' : 'CW') + d3.timeFormat('%U')(d);
  }
  if (timespan_days > 45 || (small_screen && timespan_days > 25)) {
    ticks = everyOr(d3.timeWeek as d3.CountableTimeInterval, 2);
  }
  if (timespan_days > 90 || (small_screen && timespan_days > 60)) {
    ticks = everyOr(d3.timeMonth as d3.CountableTimeInterval, 1);
    if (!small_screen) {
      const tempTicks = ticks.range(tickValuesRangeStart, tickValuesRangeEnd);
      const widthPerTick = width / Math.max(tempTicks.length, 1);
      if (widthPerTick < 35) ticks = everyOr(d3.timeMonth as d3.CountableTimeInterval, 4);
      else if (widthPerTick < 75) ticks = everyOr(d3.timeMonth as d3.CountableTimeInterval, 2);
    }
    tickFormat = (d) => locale.format(small_screen ? '%b' : '%B')(d);
  }

  const x = d3.scaleTime().domain([start, end]).range([0, width]);
  const end_sx = x(data[data.length - 1].date);

  let tickValues = ticks
    .range(tickValuesRangeStart, tickValuesRangeEnd)
    .filter(
      (d) =>
        x(d) >= (timespan_days > 90 || (small_screen && timespan_days > 60) ? 30 : 20) &&
        x(d) <= end_sx - (small_screen ? 30 : 60)
    );

  if (!data?.length) tickFormat = () => '';

  let tickCall = d3
    .axisBottom(x)
    .tickSize(height)
    .tickValues(tickValues)
    .tickPadding(12)
    .tickFormat(tickFormat as any);

  // Custom-range finer ticks
  if (timespan_days <= 15) {
    if (timespan_days > 7) {
      ticks = everyOr(
        d3.timeDay as d3.CountableTimeInterval,
        small_screen && timespan_days >= 6 ? 3 : 2
      );
      tickValues = ticks.range(tickValuesRangeStart, tickValuesRangeEnd);
      tickCall = d3
        .axisBottom(x)
        .tickSize(height)
        .tickValues(tickValues)
        .tickPadding(12)
        .tickFormat(tickFormat as any);
    } else if (timespan_hours < 3) {
      ticks = everyOr(
        d3.timeMinute as d3.CountableTimeInterval,
        small_screen ? (timespan_hours < 1 ? 60 : 30) : timespan_hours < 1 ? 30 : 15
      );
      tickValues = ticks.range(tickValuesRangeStart, tickValuesRangeEnd);
      tickCall = d3
        .axisBottom(x)
        .tickSize(height)
        .tickValues(tickValues)
        .tickPadding(12)
        .tickFormat(tickFormat as any);
    } else if (timespan_hours <= 24) {
      ticks = everyOr(
        d3.timeHour as d3.CountableTimeInterval,
        small_screen
          ? timespan_hours < 6
            ? 2
            : timespan_hours < 12
              ? 3
              : 6
          : timespan_hours < 6
            ? 1
            : timespan_hours < 12
              ? 2
              : 3
      );
      tickValues = ticks.range(tickValuesRangeStart, tickValuesRangeEnd);
      if (tickValues.length < 5 && timespan_hours < 6 && !small_screen) {
        tickValues = (tickValues as Date[])
          .flatMap((d) => [
            new Date(new Date(d).setMinutes(d.getMinutes() - 30)),
            d,
            new Date(new Date(d).setMinutes(d.getMinutes() + 30)),
          ])
          .filter((d) => d < tickValuesRangeEnd)
          .filter(
            (d) =>
              x(d) >= (timespan_days > 90 || (small_screen && timespan_days > 60) ? 30 : 20) &&
              x(d) <= end_sx - (small_screen ? 30 : 60)
          );
      }
      tickCall = d3
        .axisBottom(x)
        .tickSize(height)
        .tickValues(tickValues)
        .tickPadding(12)
        .tickFormat(tickFormat as any);
    }
  }

  return tickCall;
}
